import React, { useEffect, useState } from "react";
import { ResponsiveScatterPlot } from "@nivo/scatterplot";

// Linear regression function implemented directly in the component
const linearRegressionUtil = (data) => {
  if (!data || data.length < 2) return { slope: 0, intercept: 0, r2: 0 };

  const xSum = data.reduce((sum, point) => sum + point.x, 0);
  const ySum = data.reduce((sum, point) => sum + point.y, 0);
  const xMean = xSum / data.length;
  const yMean = ySum / data.length;

  const xxSum = data.reduce((sum, point) => sum + point.x * point.x, 0);
  const xySum = data.reduce((sum, point) => sum + point.x * point.y, 0);

  const slope =
    (xySum - (xSum * ySum) / data.length) /
    (xxSum - (xSum * xSum) / data.length);
  const intercept = yMean - slope * xMean;

  // Calculate R-squared
  const predictedY = data.map((point) => slope * point.x + intercept);
  const residualSum = data.reduce(
    (sum, point, i) => sum + Math.pow(point.y - predictedY[i], 2),
    0
  );
  const totalSum = data.reduce(
    (sum, point) => sum + Math.pow(point.y - yMean, 2),
    0
  );
  const r2 = 1 - residualSum / totalSum;

  return { slope, intercept, r2 };
};

const StepsVsHeartRateChart = ({ patientData, hasHr }) => {
  const [chartData, setChartData] = useState([]);
  const [regressionLine, setRegressionLine] = useState({
    slope: 0,
    intercept: 0,
    r2: 0,
  });
  const [insights, setInsights] = useState({
    correlation: "None",
    correlationStrength: "No",
    averageHr: 0,
    maxHr: 0,
    minHr: 0,
    stepsWithHighestHr: 0,
    stepsWithLowestHr: 0,
    activityImpact: "Not enough data",
  });

  useEffect(() => {
    if (
      patientData &&
      patientData.month_steps &&
      patientData.month_steps.length >= 4 &&
      patientData.month_steps[0] &&
      patientData.month_steps[3]
    ) {
      const stepsData = patientData.month_steps[0] || [];
      const hrData = patientData.month_steps[3] || [];

      // Create data points for the scatter plot
      const scatterData = [];

      // Match steps with heart rate data
      for (let i = 0; i < Math.min(stepsData.length, hrData.length); i++) {
        if (
          typeof stepsData[i] === "number" &&
          typeof hrData[i] === "number" &&
          stepsData[i] > 0 &&
          hrData[i] > 0
        ) {
          scatterData.push({
            id: `data-${i}`,
            x: stepsData[i],
            y: hrData[i],
            size: 8,
          });
        }
      }

      // Calculate regression line if we have enough data points
      if (scatterData.length > 2) {
        const regression = linearRegressionUtil(scatterData);
        setRegressionLine(regression);

        // Generate insights
        const heartRates = scatterData.map((point) => point.y);
        const minHr = Math.min(...heartRates);
        const maxHr = Math.max(...heartRates);
        const avgHr = Math.round(
          heartRates.reduce((sum, hr) => sum + hr, 0) / heartRates.length
        );

        const minHrPoint = scatterData.find((point) => point.y === minHr);
        const maxHrPoint = scatterData.find((point) => point.y === maxHr);

        let correlation = "None";
        let correlationStrength = "No";
        let activityImpact = "Limited impact";

        // Determine correlation direction and strength
        if (regression.slope > 0) {
          correlation = "Positive";

          if (regression.r2 > 0.7) {
            correlationStrength = "Strong";
            activityImpact = "Significant increase in heart rate with activity";
          } else if (regression.r2 > 0.4) {
            correlationStrength = "Moderate";
            activityImpact = "Moderate increase in heart rate with activity";
          } else if (regression.r2 > 0.2) {
            correlationStrength = "Weak";
            activityImpact = "Slight increase in heart rate with activity";
          } else {
            correlationStrength = "Very weak";
            activityImpact = "Minimal increase in heart rate with activity";
          }
        } else if (regression.slope < 0) {
          correlation = "Negative";

          if (regression.r2 > 0.7) {
            correlationStrength = "Strong";
            activityImpact =
              "Unusual: heart rate decreases with activity (review data)";
          } else if (regression.r2 > 0.4) {
            correlationStrength = "Moderate";
            activityImpact =
              "Unusual: heart rate tends to decrease with activity";
          } else if (regression.r2 > 0.2) {
            correlationStrength = "Weak";
            activityImpact =
              "Heart rate varies, with slight decrease during activity";
          } else {
            correlationStrength = "Very weak";
            activityImpact = "Heart rate has minimal relation to activity";
          }
        }

        setInsights({
          correlation,
          correlationStrength,
          averageHr: avgHr,
          maxHr,
          minHr,
          stepsWithHighestHr: maxHrPoint?.x || 0,
          stepsWithLowestHr: minHrPoint?.x || 0,
          activityImpact,
          rSquared: regression.r2.toFixed(2),
        });
      }

      // Create series format for Nivo
      const formattedData = [
        {
          id: "Steps vs Heart Rate",
          data: scatterData,
        },
      ];

      setChartData(formattedData);
    }
  }, [patientData]);

  // Don't show the chart if we have no HR data
  if (!hasHr) {
    return null;
  }

  // Generate regression line points for the entire x-range
  const generateRegressionPoints = () => {
    if (!chartData.length || !chartData[0].data.length) return [];

    const allXValues = chartData[0].data.map((point) => point.x);
    const minX = Math.min(...allXValues);
    const maxX = Math.max(...allXValues);

    // Create 20 evenly spaced points for the regression line
    const step = (maxX - minX) / 19;
    const points = [];

    for (let i = 0; i < 20; i++) {
      const x = minX + i * step;
      const y = regressionLine.slope * x + regressionLine.intercept;
      points.push({
        id: `regression-${i}`,
        x,
        y,
      });
    }

    return [
      {
        id: "Trend Line",
        data: points,
      },
    ];
  };

  const regressionPoints = generateRegressionPoints();

  const theme = {
    background: "transparent",
    textColor: "#333333",
    fontSize: 12,
    axis: {
      domain: { line: { stroke: "#dddddd", strokeWidth: 1 } },
      ticks: {
        line: { stroke: "#dddddd", strokeWidth: 1 },
        text: { fill: "#666666", fontSize: 11 },
      },
      legend: { text: { fill: "#666666", fontSize: 13 } },
    },
    grid: { line: { stroke: "#eeeeee", strokeWidth: 1 } },
    tooltip: {
      container: {
        background: "#ffffff",
        fontSize: 12,
        borderRadius: 3,
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.25)",
        padding: "8px 10px",
      },
    },
  };

  // Custom tooltip
  const CustomTooltip = ({ node }) => {
    return (
      <div
        style={{
          padding: "10px 12px",
          background: "#ffffff",
          border: "1px solid #ccc",
          borderRadius: "3px",
          color: "#333",
          minWidth: "180px",
        }}
      >
        <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
          Activity Data
        </div>
        <div style={{ marginBottom: "4px" }}>
          Steps: <strong>{node.data.x.toLocaleString()}</strong>
        </div>
        <div>
          Heart Rate: <strong>{node.data.y} bpm</strong>
        </div>
      </div>
    );
  };

  // If no data, show empty state
  if (!chartData.length || !chartData[0].data || chartData[0].data.length < 3) {
    return (
      <div className="graph">
        <h3>Steps vs Heart Rate Correlation</h3>
        <div style={{ padding: "20px 0", color: "#666", textAlign: "center" }}>
          Not enough data to analyze heart rate and steps correlation.
        </div>
      </div>
    );
  }

  return (
    <div className="graph">
      <h3>Steps vs Heart Rate Correlation</h3>

      {/* Insights panel */}
      <div
        style={{
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: "4px",
          padding: "12px",
          marginBottom: "20px",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          paddingTop: "10px",
          marginTop: "10px",
        }}
      >
        <div
          style={{
            flex: "1 1 300px",
            minWidth: "300px",
            borderRight: "1px solid #e2e8f0",
            paddingRight: "16px",
            marginRight: "16px",
          }}
        >
          <h4
            style={{ fontSize: "16px", margin: "0 0 10px 0", color: "#334155" }}
          >
            Correlation Analysis
          </h4>
          <p
            style={{ fontSize: "14px", margin: "0 0 8px 0", color: "#475569" }}
          >
            <strong>Finding:</strong> {insights.correlationStrength}{" "}
            {insights.correlation.toLowerCase()} correlation between steps and
            heart rate (R² = {insights.rSquared})
          </p>
          <p style={{ fontSize: "14px", margin: "0", color: "#475569" }}>
            <strong>Interpretation:</strong> {insights.activityImpact}
          </p>
        </div>

        <div style={{ flex: "1 1 300px", minWidth: "300px" }}>
          <h4
            style={{ fontSize: "16px", margin: "0 0 10px 0", color: "#334155" }}
          >
            Heart Rate Metrics
          </h4>
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            <div style={{ marginRight: "24px", marginBottom: "8px" }}>
              <div style={{ fontSize: "13px", color: "#64748b" }}>
                Average HR
              </div>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "bold",
                  color: "#334155",
                }}
              >
                {insights.averageHr} bpm
              </div>
            </div>
            <div style={{ marginRight: "24px", marginBottom: "8px" }}>
              <div style={{ fontSize: "13px", color: "#64748b" }}>
                Lowest HR
              </div>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "bold",
                  color: "#334155",
                }}
              >
                {insights.minHr} bpm
              </div>
            </div>
            <div style={{ marginBottom: "8px" }}>
              <div style={{ fontSize: "13px", color: "#64748b" }}>
                Highest HR
              </div>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "bold",
                  color: "#334155",
                }}
              >
                {insights.maxHr} bpm
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div style={{ height: "350px", width: "100%" }}>
        <ResponsiveScatterPlot
          data={chartData}
          theme={theme}
          margin={{ top: 20, right: 60, bottom: 70, left: 90 }}
          xScale={{ type: "linear", min: "auto", max: "auto" }}
          yScale={{ type: "linear", min: "auto", max: "auto" }}
          blendMode="normal"
          colors={{ scheme: "red_blue" }}
          nodeSize={8}
          useMesh={true}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: "Steps",
            legendPosition: "middle",
            legendOffset: 46,
            format: (value) =>
              value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: "Heart Rate (bpm)",
            legendPosition: "middle",
            legendOffset: -60,
          }}
          tooltip={CustomTooltip}
           /* legends={[
            {
              anchor: "top-right",
              direction: "column",
              justify: false,
              translateX: 30,
              translateY: 0,
              itemsSpacing: 0,
              itemDirection: "left-to-right",
              itemWidth: 100,
              itemHeight: 12,
              itemOpacity: 0.75,
              symbolSize: 12,
              symbolShape: "circle",
              symbolBorderColor: "rgba(0, 0, 0, .5)",
              effects: [
                {
                  on: "hover",
                  style: {
                    itemOpacity: 1,
                  },
                },
              ],
            },
          ]} */
          layers={[
            "grid",
            "axes",
            "nodes",
            "mesh",
            "legends",
            "annotations",
            // Custom layer for regression line
            ({ nodes, xScale, yScale }) => {
              if (!regressionPoints.length || !regressionPoints[0].data.length)
                return null;

              const points = regressionPoints[0].data;
              const linePath = points.reduce((path, point, i) => {
                const x = xScale(point.x);
                const y = yScale(point.y);
                return path + (i === 0 ? `M ${x},${y} ` : `L ${x},${y} `);
              }, "");

              return (
                <g>
                  <path
                    d={linePath}
                    fill="none"
                    stroke="#ff6384"
                    strokeWidth={2}
                    strokeDasharray="5,5"
                  />
                </g>
              );
            },
          ]}
        />
      </div>
    </div>
  );
};

export default StepsVsHeartRateChart;
